import Swal from 'sweetalert2';
import {
  EMPTY,
  Subject,
  catchError,
  firstValueFrom,
  map,
  skip,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { Component } from '@angular/core';
import moment from 'moment';
import { BooleanInput } from 'ng-zorro-antd/core/types';
import { IPreviewDraft } from 'src/app/interfaces/Detailed/IPreviewDraftDetailed';
import { IMedicationDetailed } from 'src/app/interfaces/Medication/IMedicationDetailed';
import { IScheduledDays } from 'src/app/interfaces/Medication/IScheduledDays';
import { date_format, datetime_format } from 'src/date/date.configuration';
import { EpisodeOfCareService } from 'src/app/services/episodeOfCare.service';
import { AllergiesExpandComponent } from '../components/alerts-section/allergies/allergies-expand/allergies-expand.component';
import { ProcedureExpandComponent } from '../components/medical-problems-section/procedure/procedure-expand/procedure-expand.component';
import { MedicalDeviceExpandComponent } from '../components/medical-problems-section/medical-device/medical-device-expand/medical-device-expand.component';
import { CurrentProblemsExpandComponent } from '../components/medical-problems-section/current-problems/current-problems-expand/current-problems-expand.component';
import { Router } from '@angular/router';
import { NzSelectOptionInterface } from 'ng-zorro-antd/select';
import {
  IDynamicItem,
  IDynamicTable,
} from 'src/app/interfaces/DynamicTable/IDynamicDetailed';
import { IQuestionnaireDetailed } from 'src/app/interfaces/Questionnaire/IQuestionnaireDetailed';
import { DynamicQuestionnaireService } from 'src/app/services/dynamicQuestionnaire.service';
import { TranslateService } from '@ngx-translate/core';
import { COLORS } from 'src/styles/colors';
import { SwalToastService } from 'src/app/services/swal.service';
import {
  IQuestionnaire,
  IQuestionnaireQuestion,
} from 'src/app/interfaces/Questionnaire/IQuestionnaire';
import { EQuestionType } from 'src/app/constants/constants';
import { GetAnswerDto } from 'src/app/interfaces/Questionnaire/IQuestionnaireAnswers';
import { findLockedQuestionnaireItems } from 'src/app/utils/questionnaire-locked-items';
import { hasBothMrcGrades, MRC_MUSCLES } from 'src/app/utils/mrc-grades';
import { CarePlanService } from 'src/app/services/carePlan.service';
import { LocaleService } from 'src/app/services/locale.service';
import { IProblemsDetailed } from 'src/app/interfaces/Problems/IProblemsDetailed';
import { IVaccinationDetailed } from 'src/app/interfaces/Vaccination/IVaccinationDetailed';

const SCHEDULED_DAY_LABELS: Array<{
  key: keyof IScheduledDays;
  label: string;
}> = [
  { key: 'monday', label: 'Mon' },
  { key: 'tuesday', label: 'Tue' },
  { key: 'wednesday', label: 'Wed' },
  { key: 'thursday', label: 'Thu' },
  { key: 'friday', label: 'Fri' },
  { key: 'saturday', label: 'Sat' },
  { key: 'sunday', label: 'Sun' },
];

@Component({
  selector: 'app-preview-summary',
  templateUrl: './preview-summary.component.html',
  styleUrls: ['./preview-summary.component.css'],
  standalone: false,
})
export class PreviewSummaryComponent {
  public allergiesExpandComp = AllergiesExpandComponent;
  public procedureExpandComp = ProcedureExpandComponent;
  public medicalDeviceExpandComp = MedicalDeviceExpandComponent;
  public problemExpandComp = CurrentProblemsExpandComponent;

  private destroy$ = new Subject<void>();
  private previewReload$ = new Subject<boolean>();

  data?: IPreviewDraft;

  switchValue?: boolean = false;

  pdValue?: number;

  pdList: NzSelectOptionInterface[] = [];

  noteValue?: string = '';

  isLoading: boolean = false;
  isPdLoading: BooleanInput = false;

  questionnaireData: any[] = [];

  questionnaireSections: Array<{
    questionnaireId: number;
    questionnaireTemplateId: number;
    title: string;
    rows: any[];
  }> = [];

  constructor(
    private readonly eocService: EpisodeOfCareService,
    private readonly questionnaireService: DynamicQuestionnaireService,
    private readonly router: Router,
    private readonly translate: TranslateService,
    private readonly swalToastService: SwalToastService,
    private readonly carePlanService: CarePlanService,
    private readonly localeService: LocaleService,
  ) {}

  handleEndedValue(value: boolean) {
    this.eocService.eocEndedSub.next(value);
  }

  setPD(id: number) {
    this.eocService.setPD(id);
  }

  setNote(note: any) {
    this.eocService.setNote(note);
  }

  isInpatientView() {
    return (
      !(
        this.eocService.currentEocId() === null ||
        this.eocService.currentEocId() === ''
      ) && this.router.url.includes('inPatientView')
    );
  }

  ngOnInit() {
    this.isLoading = true;
    this.eocService.unvalidatePreview();
    this.eocService.unsetNote();
    this.eocService.unsetPd();
    this.eocService.eocEndedSub.next(false);

    this.eocService.potentialDiagnosisSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: number | undefined) => {
        this.pdValue = data || undefined;
      });

    this.isPdLoading = true;
    this.previewReload$
      .pipe(
        tap(() => (this.isLoading = true)),
        switchMap((displayOnly) => {
          const visitId = this.eocService.currentVisitId();
          if (!visitId) {
            this.isLoading = false;
            return EMPTY;
          }

          return this.eocService.getPreviewDraft(visitId).pipe(
            map((response) => ({ displayOnly, response })),
            catchError(() => {
              this.isLoading = false;
              this.swalToastService.toastError(
                'Unable to retrieve Draft Records',
              );
              return EMPTY;
            }),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(({ displayOnly, response }) => {
        if (displayOnly && this.data) {
          this.mergeLocalizedTerminology(response?.data);
          this.isLoading = false;
          return;
        }

        void this.applyPreviewDraft(response?.data);
      });

    this.loadPreviewDraft();

    this.carePlanService.carePlanChangedSub
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.loadPreviewDraft();
      });

    this.localeService.locale$
      .pipe(skip(1), takeUntil(this.destroy$))
      .subscribe(() => this.loadPreviewDraft(true));

    this.localeService.locale$.pipe(
      tap(() => (this.isPdLoading = true)),
      switchMap(() => this.eocService.getPotentialDiagnosis()),
      takeUntil(this.destroy$),
    ).subscribe({
      next: (d) => {
        this.pdList = d?.data?.map((item) => ({
          label: item.name,
          value: item.id,
          code: item.code,
          englishName: item.englishName,
        }));
        this.isPdLoading = false;
      },
      error: () => {
        this.isPdLoading = false;
        this.swalToastService.toastError('Unable to retrieve Potential Diagnosis');
      },
    });
  }

  private loadPreviewDraft(displayOnly = false) {
    this.previewReload$.next(displayOnly);
  }

  private async applyPreviewDraft(data?: IPreviewDraft) {
    this.data = data;

    if (this.data?.getAllCurrentMedicationsDto?.getAll) {
      this.data.getAllCurrentMedicationsDto.getAll =
        this.data.getAllCurrentMedicationsDto.getAll.map((item) =>
          this.enrichMedicationForTable(item),
        );
    }

    if (this.data?.getAllPastMedicationsDto) {
      this.data.getAllPastMedicationsDto =
        this.data.getAllPastMedicationsDto.map((item) =>
          this.enrichMedicationForTable(item),
        );
    }

    if (
      this.data?.getAllCarePlans?.length ||
      this.data?.getAllActiveCarePlans?.length ||
      this.data?.getAllPastCarePlans?.length
    ) {
      try {
        const dropdownResponse = await firstValueFrom(
          this.carePlanService.getDropdownData(),
        );
        this.enrichPreviewCarePlans(dropdownResponse.data);
      } catch {
        // Keep preview usable when terminology metadata is unavailable.
      }
    }

    if (data?.getPotentialDiagnosis?.id) {
      this.eocService.setPD(data.getPotentialDiagnosis.id);
    }

    if (data?.isEmpty == false) {
      this.eocService.validatePreview();
    }

    await this.loadAllQuestionnaires();
    this.isLoading = false;
  }

  private mergeLocalizedTerminology(localized?: IPreviewDraft): void {
    if (!this.data || !localized) {
      return;
    }

    const localizedDiagnosis = localized.getPotentialDiagnosis;
    if (
      this.data.getPotentialDiagnosis &&
      localizedDiagnosis &&
      this.data.getPotentialDiagnosis.id === localizedDiagnosis.id
    ) {
      this.data.getPotentialDiagnosis = {
        ...this.data.getPotentialDiagnosis,
        name: localizedDiagnosis.name,
      };
    }

    if (this.data.getAllCurrentProblemsDto?.getAll) {
      this.data.getAllCurrentProblemsDto = {
        ...this.data.getAllCurrentProblemsDto,
        getAll: this.mergeProblemLabels(
          this.data.getAllCurrentProblemsDto.getAll,
          localized.getAllCurrentProblemsDto?.getAll,
        ),
      };
    }

    this.data.getAllResolvedProblemsDto = this.mergeProblemLabels(
      this.data.getAllResolvedProblemsDto,
      localized.getAllResolvedProblemsDto,
    );
    this.data.getAllVaccinations = this.mergeVaccinationLabels(
      this.data.getAllVaccinations,
      localized.getAllVaccinations,
    );

    if (this.data.getAllCurrentMedicationsDto?.getAll) {
      this.data.getAllCurrentMedicationsDto = {
        ...this.data.getAllCurrentMedicationsDto,
        getAll: this.mergeMedicationLabels(
          this.data.getAllCurrentMedicationsDto.getAll,
          localized.getAllCurrentMedicationsDto?.getAll,
        ),
      };
    }

    this.data.getAllPastMedicationsDto = this.mergeMedicationLabels(
      this.data.getAllPastMedicationsDto,
      localized.getAllPastMedicationsDto,
    );
  }

  private mergeProblemLabels(
    current?: IProblemsDetailed[],
    localized?: IProblemsDetailed[],
  ): IProblemsDetailed[] | undefined {
    if (!current || !localized) {
      return current;
    }

    const labels = new Map(
      localized
        .filter((item) => item.id != null)
        .map((item) => [item.id!, item]),
    );
    return current.map((item) => {
      const translated = item.id == null ? undefined : labels.get(item.id);
      return translated
        ? {
            ...item,
            problemName: translated.problemName,
            illnessAndDisorder: translated.illnessAndDisorder,
          }
        : item;
    });
  }

  private mergeVaccinationLabels(
    current?: IVaccinationDetailed[],
    localized?: IVaccinationDetailed[],
  ): IVaccinationDetailed[] | undefined {
    if (!current || !localized) {
      return current;
    }

    const labels = new Map(
      localized
        .filter((item) => item.id != null)
        .map((item) => [item.id!, item]),
    );
    return current.map((item) => {
      const translated = item.id == null ? undefined : labels.get(item.id);
      return translated
        ? { ...item, diseaseTargeted: translated.diseaseTargeted }
        : item;
    });
  }

  private mergeMedicationLabels(
    current?: IMedicationDetailed[],
    localized?: IMedicationDetailed[],
  ): IMedicationDetailed[] | undefined {
    if (!current || !localized) {
      return current;
    }

    const labels = new Map(
      localized
        .filter((item) => item.id != null)
        .map((item) => [item.id!, item.problem]),
    );
    return current.map((item) => {
      if (item.id == null || !labels.has(item.id)) {
        return item;
      }
      return { ...item, problem: labels.get(item.id) };
    });
  }

  // Builds the display-only fields the medication table needs (period range,
  // scheduled days summary, product name truncated via CSS when it overflows
  // its column) since the backend only sends the raw data these are derived
  // from.
  private enrichMedicationForTable(
    item: IMedicationDetailed,
  ): IMedicationDetailed {
    const result: IMedicationDetailed & { [key: string]: any } = {
      ...item,
      periodCombination: this.getMedicationPeriodLabel(
        item?.onSetDateTime,
        item?.endDateTime,
      ),
      scheduledDaysCombination: this.getScheduledDaysLabel(item?.scheduledDays),
    };

    if (item?.productCombination) {
      result['__badge__productCombination'] = {
        text: item.productCombination,
        className: 'truncate-cell',
        title: item.productCombination,
      };
    }

    return result;
  }

  private formatMedicationDate(value?: string): string | null {
    if (!value) {
      return null;
    }

    const parsed = moment(
      value,
      [moment.ISO_8601, datetime_format, date_format],
      true,
    );
    return parsed.isValid() ? parsed.format(date_format) : value;
  }

  private getMedicationPeriodLabel(
    onSetDateTime?: string,
    endDateTime?: string,
  ): string {
    const start = this.formatMedicationDate(onSetDateTime);

    if (!start) {
      return 'N/A';
    }

    const end = this.formatMedicationDate(endDateTime) ?? 'Present';

    return `From ${start} to ${end}`;
  }

  private getScheduledDaysLabel(scheduledDays?: IScheduledDays | null): string {
    const selectedDays = scheduledDays
      ? SCHEDULED_DAY_LABELS.filter((day) => scheduledDays[day.key])
      : [];

    if (selectedDays.length === SCHEDULED_DAY_LABELS.length) {
      return 'Everyday';
    }

    if (selectedDays.length === 0) {
      return 'N/A';
    }

    return selectedDays.map((day) => day.label).join(', ');
  }

  private enrichPreviewCarePlans(dropdownData: any) {
    if (!this.data) {
      return;
    }

    if (this.data.getAllCarePlans?.length) {
      this.data.getAllCarePlans =
        this.carePlanService.enrichTerminologyDisplays(
          this.data.getAllCarePlans,
          dropdownData,
        );
    }

    if (this.data.getAllActiveCarePlans?.length) {
      this.data.getAllActiveCarePlans =
        this.carePlanService.enrichTerminologyDisplays(
          this.data.getAllActiveCarePlans,
          dropdownData,
        );
    }

    if (this.data.getAllPastCarePlans?.length) {
      this.data.getAllPastCarePlans =
        this.carePlanService.enrichTerminologyDisplays(
          this.data.getAllPastCarePlans,
          dropdownData,
        );
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getLaboratoryData(
    laboratoryData: IDynamicTable<IDynamicItem>[],
    groupId: number,
  ) {
    return laboratoryData.filter((dataItem) => dataItem.groupId == groupId);
  }

  getCurrentCareTeamsForPreview() {
    return (this.data?.getAllCareTeams ?? [])
      .filter((careTeam) => careTeam.isActive)
      .map((careTeam) => ({
        ...careTeam,
        onSetDate: careTeam.onSetDateTime,
        endDate: careTeam.endDateTime,
      }));
  }

  getPastCareTeamsForPreview() {
    return (this.data?.getAllCareTeams ?? [])
      .filter((careTeam) => !careTeam.isActive)
      .map((careTeam) => ({
        ...careTeam,
        onSetDate: careTeam.onSetDateTime,
        endDate: careTeam.endDateTime,
      }));
  }

  private async loadAllQuestionnaires(): Promise<void> {
    const questionnaires = this.data?.getAllQuestionnaires ?? [];
    if (!questionnaires.length) {
      this.questionnaireSections = [];
      return;
    }

    // Keep spinner on while we load questionnaire details
    this.isLoading = true;

    // Keep latest questionnaire per templateId using MAX(id)
    const latestByTemplate = new Map<number, IQuestionnaireDetailed>();

    for (const q of questionnaires) {
      const existing = latestByTemplate.get(q.questionnaireTemplateId);

      if (!existing || q.id > existing.id) {
        latestByTemplate.set(q.questionnaireTemplateId, q);
      }
    }

    const uniqueQuestionnaires = Array.from(latestByTemplate.values());

    try {
      const sections = await Promise.all(
        uniqueQuestionnaires.map(async (q) => {
          const rows = await this.getQuestionnaireData(
            q.questionnaireTemplateId,
          );

          if (!rows.length) {
            return null;
          }

          return {
            questionnaireId: q.id,
            questionnaireTemplateId: q.questionnaireTemplateId,
            title: rows[0]?.title ?? '',
            rows,
          };
        }),
      );

      this.questionnaireSections = sections.filter(
        (
          s,
        ): s is {
          questionnaireId: number;
          questionnaireTemplateId: number;
          title: string;
          rows: any[];
        } => !!s && s.rows.length > 0,
      );
    } catch (e) {
      this.questionnaireSections = [];
      this.swalToastService.toastError('Unable to retrieve Questionnaire Data');
    } finally {
      this.isLoading = false;
    }
  }

  getQuestionnaireData(templateId: number): Promise<any[]> {
    return new Promise((resolve, reject) => {
      this.questionnaireService
        .getQuestionnaireData(templateId) // questionnaire template id (inpatient = 1, outpatient = 2)
        .subscribe(
          (data: IQuestionnaireDetailed) => {
            if (data && data.questionnaire && data.answers) {
              const questionnaire = data.questionnaire;
              const answers = data.answers.answer;
              // Total number of pages
              const totalNumberOfPages = questionnaire.pages.length;

              const totals =
                templateId === 2
                  ? this.computeFastTotals(answers)
                  : templateId === 16
                    ? this.computePacslacTwoTotals(questionnaire, answers)
                    : this.computeQuestionnaireCompletionTotals(
                        questionnaire,
                        answers,
                        templateId,
                      );
              const totalNumberOfQuestions = totals.totalQuestions;
              const questionsCompleted = totals.completed;

              // Number of questions remaining
              const questionsRemaining = Math.max(
                totalNumberOfQuestions - questionsCompleted,
                0,
              );

              // Completion percentage
              const completionPercentage =
                totalNumberOfQuestions > 0
                  ? (
                      (questionsCompleted / totalNumberOfQuestions) *
                      100
                    ).toFixed(2) + '%'
                  : '0%';

              // Determine the status
              const status = data.isSubmitted
                ? 'Submitted'
                : 'Not Submitted (Ready for Submission)';

              // Create the data object
              const questionaireDataDto = [
                {
                  dateTime: data.creationDate || '',
                  title: questionnaire.title,
                  totalNumberOfPages: `${totalNumberOfPages} pages`,
                  totalNumberOfQuestions: `${totalNumberOfQuestions} questions`,
                  completionPercentage: `${Math.round(
                    parseFloat(completionPercentage),
                  )}% completed`,
                  questionsCompleted: `${questionsCompleted} out of ${totalNumberOfQuestions} questions completed`,
                  questionsRemaining: `${questionsRemaining} questions remaining`,
                  status,
                },
              ];

              // Resolve the promise with the data
              resolve(questionaireDataDto);
            } else {
              resolve([]); // Resolve with an empty array if there's no data
            }
          },
          (error) => {
            reject(error); // Reject the promise in case of an error
          },
        );
    });
  }

  private computeFastTotals(answers: any[]): {
    totalQuestions: number;
    completed: number;
  } {
    const answeredIds = new Set<number>(
      answers.map((a) => Number(a.questionTemplateId)),
    );

    // Fast Protocol always has exactly 4 pages (Face, Arm, Speech, Time).
    // The side question (q47/q48) is part of its page, not a separate question.
    const totalQuestions = 4;

    // A page is complete when any main answer for it exists.
    // q47/q48 (left/right side) are sub-details of their page, not counted separately.
    let completed = 0;
    if (answeredIds.has(40) || answeredIds.has(41)) completed += 1; // Face
    if (answeredIds.has(42) || answeredIds.has(43)) completed += 1; // Arm
    if (answeredIds.has(44) || answeredIds.has(46)) completed += 1; // Speech
    if (answeredIds.has(45)) completed += 1; // Time

    return { totalQuestions, completed };
  }

  //specific completion count, mirroring the backend's
  // CalculatePacslacTwoScore/GetPacslacTwoScoredCoverage: a "section"
  // element starts a new group, and every checkbox/boolean element after
  //it belongs to that group until the next section.
  private computePacslacTwoTotals(
    questionnaire: IQuestionnaire,
    answers: GetAnswerDto[],
  ): { totalQuestions: number; completed: number } {
    const elements = questionnaire.pages.flatMap((p) => p.elements ?? []);
    const totalScoreControlName = elements.find(
      (element: IQuestionnaireQuestion) =>
        element.type === 'text' && /total/i.test(element.title ?? ''),
    )?.name;

    const answeredIds = new Set(
      (answers ?? [])
        .map((answer: GetAnswerDto) => Number(answer.questionTemplateId))
        .filter((id: number) => Number.isFinite(id)),
    );

    const groups: IQuestionnaireQuestion[][] = [];
    let currentGroup: IQuestionnaireQuestion[] | null = null;

    for (const element of elements) {
      const questionType = (element.type ?? '').toLowerCase();

      if (questionType === EQuestionType.SECTION) {
        currentGroup = [];
        groups.push(currentGroup);
        continue;
      }

      if (
        (questionType !== 'checkbox' &&
          questionType !== 'boolean' &&
          questionType !== 'text') ||
        element.name === totalScoreControlName
      ) {
        continue;
      }

      if (currentGroup) {
        currentGroup.push(element);
      } else {
        groups.push([element]);
      }
    }

    const questionGroups = groups.filter((group) => group.length > 0);
    const totalQuestions = questionGroups.length;
    const completed = questionGroups.filter((group) =>
      group.some((element: IQuestionnaireQuestion) =>
        answeredIds.has(Number(element.id)),
      ),
    ).length;

    return {
      totalQuestions,
      completed: Math.min(completed, totalQuestions),
    };
  }

  private computeQuestionnaireCompletionTotals(
    questionnaire: IQuestionnaire,
    answers: GetAnswerDto[],
    templateId: number,
  ): { totalQuestions: number; completed: number } {
    const elements = questionnaire.pages.flatMap((p) => p.elements ?? []);
    const excludedScoreNames = this.getExcludedScoreControlNames(
      elements,
      templateId,
    );
    const answerableQuestionIds = new Set(
      elements
        .filter((element: IQuestionnaireQuestion) =>
          this.isAnswerableQuestion(element, excludedScoreNames),
        )
        .map((element: IQuestionnaireQuestion) => Number(element.id))
        .filter((id: number) => Number.isFinite(id)),
    );
    const answerById = new Map<number, string>(
      (answers ?? []).map((answer: GetAnswerDto) => [
        Number(answer.questionTemplateId),
        answer.value,
      ]),
    );
    // An item the scale's own rules lock, such as a TIS item zeroed by an
    // earlier 0, scores 0 without an answer, so it counts as done, as the
    // backend counts it.
    const lockedNames = findLockedQuestionnaireItems(
      templateId,
      elements,
      (name: string) =>
        answerById.get(Number(elements.find((el) => el.name === name)?.id)),
    );
    const lockedQuestionIds = new Set(
      elements
        .filter((element: IQuestionnaireQuestion) =>
          lockedNames.has(element.name),
        )
        .map((element: IQuestionnaireQuestion) => Number(element.id)),
    );
    const nameById = new Map(
      elements.map((element: IQuestionnaireQuestion) => [
        Number(element.id),
        element.name,
      ]),
    );
    const totalQuestions = answerableQuestionIds.size;
    const completed = [...answerableQuestionIds].filter(
      (id: number) =>
        lockedQuestionIds.has(id) ||
        (answerById.has(id) &&
          this.isCompleteAnswer(
            templateId,
            nameById.get(id),
            answerById.get(id),
          )),
    ).length;

    return {
      totalQuestions,
      completed: Math.min(completed, totalQuestions),
    };
  }

  // An answer holding more than one grade counts only once every grade is
  // given, as the backend scores it: an MRC (template 18) muscle needs both
  // its left and right grades, so "4," is not done yet.
  private isCompleteAnswer(
    templateId: number,
    name: string | undefined,
    value: string | undefined,
  ): boolean {
    return templateId === 18 && MRC_MUSCLES.includes(name ?? '')
      ? hasBothMrcGrades(value)
      : true;
  }

  private isAnswerableQuestion(
    element: IQuestionnaireQuestion,
    excludedScoreNames: Set<string>,
  ): boolean {
    const questionType = (element.type ?? '').toLowerCase();

    return (
      questionType !== EQuestionType.SECTION &&
      questionType !== EQuestionType.HEADER &&
      !excludedScoreNames.has(element.name)
    );
  }

  private getExcludedScoreControlNames(
    elements: IQuestionnaireQuestion[],
    templateId: number,
  ): Set<string> {
    const knownScoreControlNames: Record<number, string[]> = {
      3: ['question62'],
      5: ['question86'],
      6: ['question108'],
      8: ['question458'],
      // MFIS: physical, cognitive, psychosocial, total
      17: ['question675', 'question676', 'question677', 'question678'],
      // MRC: total
      18: ['question685'],
      // Mini-BESTest: four sub scores and the total
      19: [
        'question694',
        'question701',
        'question706',
        'question715',
        'question716',
      ],
      // Fugl-Meyer: subtotals I-V and the totals of A, B, C, D, A-D, H and J
      20: [
        'question720',
        'question733',
        'question738',
        'question743',
        'question746',
        'question747',
        'question753',
        'question762',
        'question766',
        'question767',
        'question774',
        'question787',
        'question801',
      ],
      // Trunk Control Test: total
      21: ['question806'],
      // Trunk Impairment Scale: three subscales and the total
      22: ['question810', 'question821', 'question826', 'question827'],
      // Functional Gait Assessment: total
      26: ['question849'],
      // Modified Ashworth Scale: 853 only lists the grades and is never
      // answered; every row is saved in 852
      28: ['question853'],
      // Barthel Index: total, only shown and never saved
      29: ['question864'],
      // FIM: motor and cognitive subtotals and the total
      30: ['question883', 'question893', 'question894'],
      // Motricity Index: each side's arm, leg and side scores
      31: [
        'question900',
        'question905',
        'question906',
        'question912',
        'question917',
        'question918',
      ],
    };
    const excludedNames = new Set<string>(
      knownScoreControlNames[templateId] ?? [],
    );

    if (templateId === 10) {
      const sldcTotalControlName = elements.find(
        (element: IQuestionnaireQuestion) =>
          element.type !== 'radiogroup' && !!element.name,
      )?.name;

      if (sldcTotalControlName) {
        excludedNames.add(sldcTotalControlName);
      }
    }

    return excludedNames;
  }
}
